import clsx from 'clsx';
import type { ComponentProps, FunctionComponent } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { Badge } from '~/ui/core/badge/Badge';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';

import {
    formatPercent,
    POTENTIAL_BANDS,
    type PotentialBand,
    potentialBandIndex,
    potentialBandTranslationKey,
} from '../../ai-transformation/model/automation';
import { PositionRetryMessage } from '../components/PositionMessage';
import type { PositionAutomation } from './position-automation';
import type { PositionAutomationRead } from './use-position-automation';

/** The share of the scale each band takes, matching the band edges of the AI transformation model. */
const BAND_WIDTHS: Record<PotentialBand, number> = {
    HIGH: 0.25,
    LOW: 0.175,
    MODERATE: 0.225,
    NONE: 0.075,
    VERY_HIGH: 0.275,
};

/** The badge each potential band wears in the task list; the scale above colours the band the same way. */
export const BAND_BADGE: Record<PotentialBand, ComponentProps<typeof Badge>['variant']> = {
    HIGH: 'error',
    LOW: 'info',
    MODERATE: 'warning',
    NONE: 'gray',
    VERY_HIGH: 'error',
};

const BAND_FILL: Record<PotentialBand, string> = {
    HIGH: 'bg-border-error-strong',
    LOW: 'bg-status-info-text',
    MODERATE: 'bg-status-pending',
    NONE: 'bg-status-neutral-text',
    VERY_HIGH: 'bg-border-error-strong',
};

export const formatHours = (value: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value);

const BandScale: FunctionComponent<{ readonly band: PotentialBand; readonly score: number }> = ({ band, score }) => {
    const translate = useTranslate();
    const active = potentialBandIndex(band);

    return (
        <div className="flex flex-col gap-xs">
            <div aria-hidden={true} className="relative flex h-2 items-center gap-[2px]">
                {POTENTIAL_BANDS.map((entry, index) => (
                    <span
                        key={entry}
                        className={clsx('h-1 rounded-full', index === active ? BAND_FILL[band] : 'bg-border-subtle')}
                        style={{ width: `${BAND_WIDTHS[entry] * 100}%` }}
                    />
                ))}
                <span
                    className={clsx(
                        'absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface-raised',
                        BAND_FILL[band],
                    )}
                    style={{ left: `${Math.min(Math.max(score, 0), 1) * 100}%` }}
                />
            </div>
            <div className="flex justify-between text-body-s text-text-muted">
                <span>{translate(potentialBandTranslationKey('NONE'))}</span>
                <span>{translate(potentialBandTranslationKey('VERY_HIGH'))}</span>
            </div>
        </div>
    );
};

const AutomationHeadline: FunctionComponent<{ readonly automation: PositionAutomation; readonly locale: string }> = ({
    automation,
    locale,
}) => {
    const translate = useTranslate();

    return (
        <p className="m-0 flex items-baseline gap-s">
            <span className="text-h4 text-text-primary" data-testid="position-automation-band">
                {translate(potentialBandTranslationKey(automation.band))}
            </span>
            <span className="text-body-s tabular-nums text-text-muted">
                {formatPercent(automation.score, locale)} ({formatPercent(automation.scoreLower, locale)}–
                {formatPercent(automation.scoreUpper, locale)})
            </span>
        </p>
    );
};

export const PositionAutomationBrief: FunctionComponent<{ readonly read: PositionAutomationRead }> = ({ read }) => {
    const translate = useTranslate();
    const {
        applicationState: { language },
    } = useApplicationContext();
    const locale = language.code;

    if (read.status === 'unread' || (read.status !== 'loading' && read.automation.taskCount === 0)) return null;

    const automation = read.status === 'read' ? read.automation : undefined;
    const title = translate('pages.position-management.detail.automation.title');

    return (
        <section
            aria-label={title}
            className="flex flex-col gap-l rounded-xl bg-surface-raised p-xxl shadow-sm"
            data-testid="position-automation-brief"
        >
            <div className="flex items-end justify-between gap-m">
                <div className="flex min-w-0 flex-col gap-xxs">
                    <h3 className="m-0 text-body-s font-normal text-text-muted">{title}</h3>
                    {read.status === 'loading' ? <Skeleton height="1.75rem" width="10rem" /> : null}
                    {automation?.score === undefined ? null : (
                        <AutomationHeadline automation={automation} locale={locale} />
                    )}
                </div>
            </div>
            {read.status === 'failed' ? (
                <PositionRetryMessage
                    isRetrying={read.isRetrying}
                    message={translate('pages.ai-transformation.chart.error')}
                    onRetry={read.retry}
                />
            ) : null}
            {automation !== undefined && automation.score === undefined ? (
                <p className="m-0 text-body-s text-text-muted">
                    {translate('pages.position-management.detail.automation.not-assessed')}
                </p>
            ) : null}
            {automation?.band !== undefined && automation.score !== undefined ? (
                <BandScale band={automation.band} score={automation.score} />
            ) : null}
        </section>
    );
};
